import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import { CmpCheck, CmpDash } from '../components/icons';
import { AWNING_WARRANTY_YEARS, EXPECTED_LIFE_YEARS } from '../config/warranty';
import './ComparePage.css';
import { apiFetch } from '../services/api';
import { isPrerendering, fallbackProducts, fallbackAccessories } from '../prerenderData';
import { cardImage } from '../utils/productImages';

// Local aliases preserve the existing variable names used through
// the table cells below. The underlying icons live in
// components/icons.js per CQ-23 Phase B. StarIcon stays local —
// it's only used on this page.
const CheckIcon = () => <CmpCheck size={20} />;
const DashIcon = () => <CmpDash size={20} />;
const StarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="#f59e0b" stroke="#f59e0b" strokeWidth="1"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
);

/* ── pergola comparison data ── */
const MODEL_DATA = {
  'pergola-163': {
    badge: 'Compact',
    badgeColor: '#2563eb',
    tagline: 'The Compact',
    controls: { label: 'Electric Louvers', type: 'smart' },
    setupTime: '3 to 6 Hours',
    motorised: true,
    led: { label: 'LED Strips', desc: 'Optional on flat louver models' },
    loadCapacity: 'Standard',
    bladeWidth: '163mm',
    maxSize: '4m × 4m',
    drainage: 'Hidden system',
    postSize: '120×120mm',
    custom: '3×3 · 3×4 · 3×6 · 4×4',
    colours: ['#6b7280', '#f5f5f4', '#78716c', '#1c1917'],
  },
  'pergola-175': {
    badge: 'Best Seller',
    badgeColor: 'var(--asg-cta)',
    tagline: 'The Standard',
    controls: { label: 'Electric Louvers', type: 'smart' },
    setupTime: '3 to 7 Hours',
    motorised: true,
    led: { label: 'LED Strips', desc: 'Optional on all four sides' },
    loadCapacity: '100kg/m²',
    bladeWidth: '175mm',
    maxSize: '5m × 4m',
    drainage: 'Dual hidden system',
    postSize: '150×150mm or 180×180mm',
    custom: 'Fully custom',
    colours: ['#6b7280', '#f5f5f4', '#78716c', '#1c1917'],
  },
  'pergola-180-retractable': {
    badge: 'Innovative',
    badgeColor: '#7c3aed',
    tagline: 'The Retractable',
    controls: { label: 'Smart Controls', type: 'smart' },
    setupTime: '4 to 8 Hours',
    motorised: true,
    led: { label: 'LED Strips', desc: 'Beam-integrated lighting' },
    loadCapacity: '100kg/m²',
    bladeWidth: '181mm',
    maxSize: '8m × 4m',
    drainage: 'Hidden system',
    postSize: 'Aluminium alloy',
    custom: 'Fully custom',
    colours: ['#6b7280', '#f5f5f4', '#78716c', '#1c1917'],
  },
};

/* ── awning comparison data ── */
const AWNING_DATA = {
  'aw01-full-box-awning': {
    badge: 'Most Popular',
    badgeColor: '#0f172a',
    tagline: 'The Cassette',
    aesthetic: 'Modern, fully enclosed',
    valance: 'None (cassette)',
    armMechanism: 'Stainless chain link',
    led: { label: 'Cassette and arms', desc: 'LED strips along the cassette and each arm, standard' },
    cassette: 'Yes, full cassette',
    drainage: 'Hidden system',
    armWidth: '60.73 mm',
    profileThickness: '2.27 mm',
    widthRange: '2.0 to 7.0 m',
    projectionRange: '1.0 to 3.5 m',
    maxSize: '7.0 × 3.5 m',
    colourOptions: 'White or frosted black',
    fabric: `Spanish Sauleda, ${AWNING_WARRANTY_YEARS}-yr warranty, ${EXPECTED_LIFE_YEARS}+ yr life`,
    components: 'Full cassette',
  },
  'b06-premium-open-arm-awning': {
    badge: 'New generation',
    badgeColor: 'var(--asg-cta)',
    tagline: 'The Modern',
    aesthetic: 'Modern open + LED',
    valance: 'None - clean front bar',
    armMechanism: 'Stainless chain link',
    led: { label: 'Integrated in arms', desc: 'Waterproof LED strips' },
    cassette: 'No',
    drainage: 'Standard',
    armWidth: '55.44 / 64.69 mm',
    profileThickness: '2.27 mm',
    widthRange: '2.0 to 6.0 m',
    projectionRange: '1.0 to 3 m',
    maxSize: '6.0 × 3 m',
    colourOptions: 'Standard',
    fabric: 'Acrylic-coated, Sauleda optional',
    components: '10-part arm set',
  },
  'a09-long-projection-awning': {
    badge: 'Deepest projection',
    badgeColor: '#7c3aed',
    tagline: 'The Specialist',
    aesthetic: 'Heavy-gauge arms, traditional',
    valance: 'Straight with corner cutouts',
    armMechanism: 'Independent heavy-gauge arms',
    led: { label: 'Not available', desc: '' },
    cassette: 'No',
    drainage: 'Standard',
    armWidth: '82.13 mm (largest)',
    profileThickness: '3.27 mm (thickest)',
    widthRange: '2.0 to 6.0 m',
    projectionRange: '1.0 to 4 m',
    maxSize: '6.0 × 4 m',
    colourOptions: 'Standard',
    fabric: 'Acrylic-coated, Sauleda optional',
    components: 'Reinforced heavy-gauge arms, double plate bracket',
  },
  'b05-traditional-scalloped-awning': {
    badge: 'Colour choice',
    badgeColor: '#2563eb',
    tagline: 'The Mid-Tier',
    aesthetic: 'Traditional + colour',
    valance: 'Scalloped',
    armMechanism: 'Stainless chain link',
    led: { label: 'Not available', desc: '' },
    cassette: 'No',
    drainage: 'Standard',
    armWidth: '65.14 mm',
    profileThickness: '1.44 mm',
    widthRange: '2.0 to 6.0 m',
    projectionRange: '1.0 to 3 m',
    maxSize: '6.0 × 3 m',
    colourOptions: 'White or frosted black',
    fabric: 'Acrylic-coated, Sauleda optional',
    components: '8-part arm set',
  },
  'k3-heritage-mediterranean-awning': {
    badge: 'Heritage entry',
    badgeColor: '#92400e',
    tagline: 'The Heritage',
    aesthetic: 'Mediterranean / period',
    valance: 'Pointed / triangular tabs',
    armMechanism: 'Double wire link',
    led: { label: 'Not available', desc: '' },
    cassette: 'No',
    drainage: 'Standard',
    armWidth: '64.15 mm',
    profileThickness: '1.32 mm (slimmest)',
    widthRange: '2.0 to 6.0 m',
    projectionRange: '1.0 to 3 m',
    maxSize: '6.0 × 3 m',
    colourOptions: 'Standard',
    fabric: 'Acrylic-coated, Sauleda optional',
    components: '6-part arm set',
  },
};

function ComparePage() {
  // Seeded from the build-time snapshot so hydration adopts the pre-rendered
  // tables rather than rebuilding them from empty; live data follows.
  const [products, setProducts] = useState([...fallbackProducts, ...fallbackAccessories]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isPrerendering) return;
    apiFetch('/api/products')
      .then(r => r.json())
      .then(data => {
        // Only non-accessory products, sorted by sort_order
        setProducts(data.filter(p => p.category !== 'accessory'));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="cmp-loading"><div className="cmp-spinner"></div></div>;

  const pergolas = products.filter(p => p.category !== 'awning').sort((a, b) => (a.sort_order || 99) - (b.sort_order || 99));
  const awnings = products.filter(p => p.category === 'awning').sort((a, b) => (a.sort_order || 99) - (b.sort_order || 99));

  return (
    <div className="compare-page">
      <SEO
        title="Compare Awning &amp; Pergola Models - Side by Side"
        description="Compare every awning and pergola model side by side - specs, sizes and features. Awnings include free delivery to most of Melbourne; pergolas supply only."
        keywords="compare awnings, awning comparison, compare pergolas, pergola comparison, AW01 B06 A09 B05 K3, aluminium pergola comparison"
        canonical="/products/compare"
        jsonLd={[SEO.breadcrumbSchema([{ name: 'Products', url: '/products' }, { name: 'Compare Models' }]), SEO.webPageSchema({ type: 'WebPage', name: 'Compare Awning and Pergola Models', description: 'Side-by-side comparison of all ASG Shading awning and pergola models.', url: '/products/compare' })]}
      />

      {/* Hero */}
      <section className="cmp-hero">
        <div className="cmp-hero-bg"></div>
        <div className="cmp-hero-content">
          <p className="cmp-label">Product Comparison</p>
          <h1 className="cmp-title">Compare Our Range</h1>
          <p className="cmp-subtitle">Awnings (free delivery to most of Melbourne) and pergolas (supply only) - all models, side by side.</p>
        </div>
      </section>

      <Breadcrumbs items={[{ name: 'Products', url: '/products' }, { name: 'Compare Models' }]} />

      {/* AWNING SECTION */}
      {awnings.length > 0 && (
        <>
          <section className="cmp-section-header">
            <div className="cmp-section-header-inner">
              <p className="cmp-label">Awnings - Free Delivery to Most of Melbourne</p>
              <h2>The 5-model retractable awning range</h2>
              <p>From the cassette flagship to the heritage Mediterranean entry.</p>
            </div>
          </section>

          <section className="cmp-cards-section">
            <div className="cmp-scroll-wrapper">
              <div className="cmp-cards-grid" style={{ gridTemplateColumns: `repeat(${awnings.length}, minmax(240px, 1fr))` }}>
                {awnings.map(product => {
                  const meta = AWNING_DATA[product.slug] || {};
                  return (
                    <div className="cmp-card" key={product._id}>
                      {meta.badge && (
                        <span className="cmp-badge" style={{ background: meta.badgeColor }}>{meta.badge}</span>
                      )}
                      <div className="cmp-card-img">
                        {cardImage(product) ? (
                          <img src={cardImage(product)} alt={product.name} width={400} height={267} loading="lazy" />
                        ) : (
                          <div className="cmp-card-img-placeholder"></div>
                        )}
                      </div>
                      {meta.tagline && <p className="cmp-tagline">{meta.tagline}</p>}
                      <h3 className="cmp-card-name">{product.name}</h3>
                      {product.short_description && (
                        <p className="cmp-card-desc">{product.short_description}</p>
                      )}
                      <Link to={`/products/${product.slug}`} className="cmp-card-btn">View Details</Link>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="cmp-table-section">
            <h2 className="cmp-table-heading">Awning Feature Comparison</h2>
            <div className="cmp-scroll-wrapper">
              <table className="cmp-table">
                <thead>
                  <tr>
                    <th className="cmp-th-label">Feature</th>
                    {awnings.map(p => (<th key={p._id}>{p.name}</th>))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="cmp-row-label">Aesthetic</td>
                    {awnings.map(p => { const m = AWNING_DATA[p.slug] || {}; return (<td key={p._id}><strong>{m.aesthetic || '-'}</strong></td>); })}
                  </tr>
                  <tr>
                    <td className="cmp-row-label">Valance Style</td>
                    {awnings.map(p => { const m = AWNING_DATA[p.slug] || {}; return (<td key={p._id}><strong>{m.valance || '-'}</strong></td>); })}
                  </tr>
                  <tr>
                    <td className="cmp-row-label">Arm Mechanism</td>
                    {awnings.map(p => { const m = AWNING_DATA[p.slug] || {}; return (<td key={p._id}><strong>{m.armMechanism || '-'}</strong></td>); })}
                  </tr>
                  <tr>
                    <td className="cmp-row-label">Cassette Enclosure</td>
                    {awnings.map(p => { const m = AWNING_DATA[p.slug] || {}; return (<td key={p._id}>{m.cassette && m.cassette !== 'No' ? <><CheckIcon /><span className="cmp-sub">{m.cassette}</span></> : <><DashIcon /><span className="cmp-sub">Open arm</span></>}</td>); })}
                  </tr>
                  <tr>
                    <td className="cmp-row-label">Integrated LED</td>
                    {awnings.map(p => { const m = AWNING_DATA[p.slug] || {}; return (<td key={p._id}>{m.led?.label === 'Not available' ? <><DashIcon /><span className="cmp-sub">Not available</span></> : <><CheckIcon /><span className="cmp-sub">{m.led?.desc || m.led?.label}</span></>}</td>); })}
                  </tr>
                  <tr>
                    <td className="cmp-row-label">Hidden Drainage</td>
                    {awnings.map(p => { const m = AWNING_DATA[p.slug] || {}; return (<td key={p._id}>{m.drainage === 'Hidden system' ? <><CheckIcon /><span className="cmp-sub">Hidden system</span></> : <><DashIcon /><span className="cmp-sub">Standard</span></>}</td>); })}
                  </tr>
                  <tr>
                    <td className="cmp-row-label">Colour Options</td>
                    {awnings.map(p => { const m = AWNING_DATA[p.slug] || {}; return (<td key={p._id}><strong>{m.colourOptions || '-'}</strong></td>); })}
                  </tr>
                  <tr>
                    <td className="cmp-row-label">Width Range</td>
                    {awnings.map(p => { const m = AWNING_DATA[p.slug] || {}; return (<td key={p._id}><strong className="cmp-metric">{m.widthRange || '-'}</strong></td>); })}
                  </tr>
                  <tr>
                    <td className="cmp-row-label">Projection Range</td>
                    {awnings.map(p => { const m = AWNING_DATA[p.slug] || {}; return (<td key={p._id}><strong className="cmp-metric">{m.projectionRange || '-'}</strong></td>); })}
                  </tr>
                  <tr>
                    <td className="cmp-row-label">Max Single Set (W × P)</td>
                    {awnings.map(p => { const m = AWNING_DATA[p.slug] || {}; return (<td key={p._id}><strong className="cmp-metric">{m.maxSize || '-'}</strong></td>); })}
                  </tr>
                  <tr>
                    <td className="cmp-row-label">Arm Width</td>
                    {awnings.map(p => { const m = AWNING_DATA[p.slug] || {}; return (<td key={p._id}><strong>{m.armWidth || '-'}</strong></td>); })}
                  </tr>
                  <tr>
                    <td className="cmp-row-label">Profile Thickness</td>
                    {awnings.map(p => { const m = AWNING_DATA[p.slug] || {}; return (<td key={p._id}><strong>{m.profileThickness || '-'}</strong></td>); })}
                  </tr>
                  <tr>
                    <td className="cmp-row-label">Component Set</td>
                    {awnings.map(p => { const m = AWNING_DATA[p.slug] || {}; return (<td key={p._id}><strong>{m.components || '-'}</strong></td>); })}
                  </tr>
                  <tr>
                    <td className="cmp-row-label">Fabric</td>
                    {awnings.map(p => { const m = AWNING_DATA[p.slug] || {}; return (<td key={p._id}><span className="cmp-sub">{m.fabric || '-'}</span></td>); })}
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="cmp-recommend">
            <div className="cmp-recommend-inner">
              <h2>Which awning is right for you?</h2>
              <div className="cmp-recommend-grid">
                <div className="cmp-recommend-card">
                  <StarIcon />
                  <h3>Most premium</h3>
                  <p>The <strong>AW01 Full Cassette</strong> is our flagship - fully enclosed cassette, integrated LED, Spanish Sauleda fabric. Up to 7 m wide.</p>
                  <Link to="/products/aw01-full-box-awning">View AW01 →</Link>
                </div>
                <div className="cmp-recommend-card">
                  <StarIcon />
                  <h3>Deepest projection</h3>
                  <p>The <strong>A09</strong> reaches up to 4 m out from the wall with reinforced heavy-gauge arms - for deep alfresco areas.</p>
                  <Link to="/products/a09-long-projection-awning">View A09 →</Link>
                </div>
                <div className="cmp-recommend-card">
                  <StarIcon />
                  <h3>Heritage homes</h3>
                  <p>The <strong>K3</strong> features a traditional pointed-tab Mediterranean valance - perfect for Federation, period weatherboard, and villa-style homes.</p>
                  <Link to="/products/k3-heritage-mediterranean-awning">View K3 →</Link>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* PERGOLA SECTION */}
      {pergolas.length > 0 && (
        <section className="cmp-section-header">
          <div className="cmp-section-header-inner">
            <p className="cmp-label">Pergolas - Supply Only</p>
            <h2>The pergola range</h2>
            <p>Built to your spec, supplied to your address, ready for your licensed builder to install.</p>
          </div>
        </section>
      )}

      {/* Product cards */}
      <section className="cmp-cards-section" style={{ display: pergolas.length === 0 ? 'none' : undefined }}>
        <div className="cmp-scroll-wrapper">
          <div className="cmp-cards-grid" style={{ gridTemplateColumns: `repeat(${pergolas.length}, 1fr)` }}>
            {pergolas.map(product => {
              const meta = MODEL_DATA[product.slug] || {};
              return (
                <div className="cmp-card" key={product._id}>
                  {meta.badge && (
                    <span className="cmp-badge" style={{ background: meta.badgeColor }}>{meta.badge}</span>
                  )}
                  <div className="cmp-card-img">
                    {cardImage(product) ? (
                      <img src={cardImage(product)} alt={product.name} width={400} height={267} loading="lazy" />
                    ) : (
                      <div className="cmp-card-img-placeholder"></div>
                    )}
                  </div>
                  {meta.tagline && <p className="cmp-tagline">{meta.tagline}</p>}
                  <h2 className="cmp-card-name">{product.name}</h2>
                  {product.short_description && (
                    <p className="cmp-card-desc">{product.short_description}</p>
                  )}
                  {meta.colours && (
                    <div className="cmp-colours">
                      {meta.colours.map((c, i) => (
                        <span key={i} className="cmp-colour-dot" style={{ background: c }} title={['Grey', 'White', 'Coffee', 'Black'][i]}></span>
                      ))}
                    </div>
                  )}
                  <Link to={`/products/${product.slug}`} className="cmp-card-btn">View Details</Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="cmp-table-section">
        <h2 className="cmp-table-heading">Feature Comparison</h2>
        <div className="cmp-scroll-wrapper">
          <table className="cmp-table">
            <thead>
              <tr>
                <th className="cmp-th-label">Feature</th>
                {pergolas.map(p => (
                  <th key={p._id}>{p.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Category */}
              <tr>
                <td className="cmp-row-label">Category</td>
                {pergolas.map(p => (
                  <td key={p._id}>
                    <span className="cmp-cat-badge">{p.category === 'flat-blade' ? 'Flat Blade' : p.category === 'retractable' ? 'Retractable' : 'Louvered'}</span>
                  </td>
                ))}
              </tr>

              {/* Customisation */}
              <tr>
                <td className="cmp-row-label">
                  <div className="cmp-feature-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 20h9M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" /></svg>
                  </div>
                  Customisation
                </td>
                {pergolas.map(p => {
                  const meta = MODEL_DATA[p.slug] || {};
                  return (
                    <td key={p._id}>
                      <strong>{meta.custom || '-'}</strong>
                      {meta.custom === 'Fully custom' && <span className="cmp-sub">Made to your dimensions</span>}
                      {meta.custom && meta.custom !== 'Fully custom' && <span className="cmp-sub">Standard sizes only</span>}
                    </td>
                  );
                })}
              </tr>

              {/* Controls */}
              <tr>
                <td className="cmp-row-label">
                  <div className="cmp-feature-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="5" y="2" width="14" height="20" rx="3" /><circle cx="12" cy="16" r="1" /></svg>
                  </div>
                  Controls
                </td>
                {pergolas.map(p => {
                  const meta = MODEL_DATA[p.slug] || {};
                  return (
                    <td key={p._id}>
                      <strong>{meta.controls?.label || '-'}</strong>
                      {meta.controls?.type === 'smart' && <span className="cmp-sub">Remote, App or Voice</span>}
                      {meta.controls?.type === 'manual' && <span className="cmp-sub">Manual or upgrade to electric</span>}
                    </td>
                  );
                })}
              </tr>

              {/* Setup Time */}
              <tr>
                <td className="cmp-row-label">
                  <div className="cmp-feature-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                  </div>
                  Setup Time
                </td>
                {pergolas.map(p => {
                  const meta = MODEL_DATA[p.slug] || {};
                  return (
                    <td key={p._id}>
                      <strong>{meta.setupTime || '-'}</strong>
                      <span className="cmp-sub">With 2 people</span>
                    </td>
                  );
                })}
              </tr>

              {/* Motorised Roof */}
              <tr>
                <td className="cmp-row-label">
                  <div className="cmp-feature-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" /></svg>
                  </div>
                  Motorised Roof
                </td>
                {pergolas.map(p => {
                  const meta = MODEL_DATA[p.slug] || {};
                  return (
                    <td key={p._id}>
                      {meta.motorised ? (
                        <><CheckIcon /><span className="cmp-sub">Remote, App or Voice</span></>
                      ) : (
                        <><DashIcon /><span className="cmp-sub">Manual operation</span></>
                      )}
                    </td>
                  );
                })}
              </tr>

              {/* LED Lighting */}
              <tr>
                <td className="cmp-row-label">
                  <div className="cmp-feature-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 18h6M10 22h4M12 2a7 7 0 0 1 4 12.9V17H8v-2.1A7 7 0 0 1 12 2Z" /></svg>
                  </div>
                  LED Lighting
                </td>
                {pergolas.map(p => {
                  const meta = MODEL_DATA[p.slug] || {};
                  return (
                    <td key={p._id}>
                      <strong>{meta.led?.label || '-'}</strong>
                      {meta.led?.desc && <span className="cmp-sub">{meta.led.desc}</span>}
                    </td>
                  );
                })}
              </tr>

              {/* Load Capacity */}
              <tr>
                <td className="cmp-row-label">
                  <div className="cmp-feature-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82Z" /><line x1="7" y1="7" x2="7.01" y2="7" strokeWidth="2.5" /></svg>
                  </div>
                  Load Capacity
                </td>
                {pergolas.map(p => {
                  const meta = MODEL_DATA[p.slug] || {};
                  return (
                    <td key={p._id}>
                      <strong className="cmp-metric">{meta.loadCapacity || '-'}</strong>
                      <span className="cmp-sub">Snow/wind load rating</span>
                    </td>
                  );
                })}
              </tr>

              {/* Blade Width */}
              <tr>
                <td className="cmp-row-label">
                  <div className="cmp-feature-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 6h20M2 12h20M2 18h20" /></svg>
                  </div>
                  Blade Width
                </td>
                {pergolas.map(p => {
                  const meta = MODEL_DATA[p.slug] || {};
                  return (
                    <td key={p._id}><strong className="cmp-metric">{meta.bladeWidth || '-'}</strong></td>
                  );
                })}
              </tr>

              {/* Max Size */}
              <tr>
                <td className="cmp-row-label">
                  <div className="cmp-feature-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 3v18" /></svg>
                  </div>
                  Max Single Set
                </td>
                {pergolas.map(p => {
                  const meta = MODEL_DATA[p.slug] || {};
                  return (
                    <td key={p._id}><strong className="cmp-metric">{meta.maxSize || '-'}</strong></td>
                  );
                })}
              </tr>

              {/* Drainage */}
              <tr>
                <td className="cmp-row-label">
                  <div className="cmp-feature-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" /></svg>
                  </div>
                  Drainage
                </td>
                {pergolas.map(p => {
                  const meta = MODEL_DATA[p.slug] || {};
                  return (
                    <td key={p._id}>
                      <strong>{meta.drainage || '-'}</strong>
                      <span className="cmp-sub">100% rainproof when closed</span>
                    </td>
                  );
                })}
              </tr>

              {/* Post/Column */}
              <tr>
                <td className="cmp-row-label">
                  <div className="cmp-feature-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="9" y="2" width="6" height="20" rx="1" /></svg>
                  </div>
                  Posts / Columns
                </td>
                {pergolas.map(p => {
                  const meta = MODEL_DATA[p.slug] || {};
                  return (
                    <td key={p._id}><strong>{meta.postSize || '-'}</strong></td>
                  );
                })}
              </tr>

              {/* Retractable */}
              <tr>
                <td className="cmp-row-label">
                  <div className="cmp-feature-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="7 13 12 18 17 13" /><polyline points="7 6 12 11 17 6" /></svg>
                  </div>
                  Retractable Roof
                </td>
                {pergolas.map(p => (
                  <td key={p._id}>
                    {p.category === 'retractable' ? (
                      <><CheckIcon /><span className="cmp-sub">Folds completely open</span></>
                    ) : (
                      <><DashIcon /><span className="cmp-sub">Fixed louvered roof</span></>
                    )}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Recommendation */}
      <section className="cmp-recommend">
        <div className="cmp-recommend-inner">
          <h2>Which model is right for you?</h2>
          <div className="cmp-recommend-grid">
            <div className="cmp-recommend-card">
              <StarIcon />
              <h3>Best for most homes</h3>
              <p>The <strong>Pergola 175</strong> is our standard model - the best balance of size, features, and value for typical residential projects.</p>
              <Link to="/products/pergola-175">View Pergola 175 →</Link>
            </div>
            <div className="cmp-recommend-card">
              <StarIcon />
              <h3>Best for compact spaces</h3>
              <p>The <strong>Pergola 163</strong> fits perfectly in smaller courtyards and terraces. Multiple louver styles to match your aesthetic.</p>
              <Link to="/products/pergola-163">View Pergola 163 →</Link>
            </div>
            <div className="cmp-recommend-card">
              <StarIcon />
              <h3>Best for retractable roofs</h3>
              <p>The <strong>Pergola 180 Retractable</strong> folds fully open to the sky, with 181mm blades and integrated LED - perfect when you want full sun on demand.</p>
              <Link to="/products/pergola-180-retractable">View Pergola 180 →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cmp-cta">
        <div className="cmp-cta-inner">
          <h2>Still deciding? We'll help.</h2>
          <p>Send us a photo and your measurements and our team will recommend the best model for your space, budget, and lifestyle - no obligation.</p>
          <Link to="/contact" className="cmp-cta-btn">Get a Quote</Link>
        </div>
      </section>
    </div>
  );
}

export default ComparePage;
